import type { ButtonHTMLAttributes } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'md' | 'sm';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  busy?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:brightness-110',
  secondary: 'border-line bg-surface text-ink hover:bg-subtle',
  ghost: 'bg-transparent text-muted hover:bg-subtle hover:text-ink',
};

const SIZES: Record<ButtonSize, string> = {
  md: 'min-h-10 px-4 text-sm',
  sm: 'min-h-8 px-3 text-label',
};

/** The look of a button, for a link that should look like one too (ButtonLink). */
export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md'): string {
  return cn(
    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-control border border-transparent font-semibold whitespace-nowrap no-underline disabled:cursor-not-allowed disabled:opacity-55',
    VARIANTS[variant],
    SIZES[size],
  );
}

export function Button({
  variant = 'primary',
  size = 'md',
  busy = false,
  disabled,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={cn(buttonClass(variant, size), className)}
      disabled={disabled || busy}
      aria-busy={busy}
    >
      {children}
    </button>
  );
}
